import type { Dispatch, SetStateAction } from 'react'
import { Button, QRCode, Row, SecretCodes, Stack, StatusBanner, Text } from '../../../components/index.js'
import { ENROL_STEPS, NO_CAMERA, SCAN_IT, TYPE_THE_KEY, stepCount } from '../core/constants.js'
import type { Enrolment } from '../core/types.js'
import { CodeField } from './CodeField.js'

/**
 * Setting up an authenticator app: the QR, the key beside it for anybody whose camera
 * will not read it, and ten recovery codes shown once — then the six digits that prove
 * it worked.
 *
 * Lifted out of `SignIn.tsx` so a brand-new administrator accepting a team invitation
 * (`TeamInvite.tsx`) goes through exactly the same screen a reset or a first ordinary
 * sign-in does, rather than a second implementation of the one thing in this product
 * that genuinely cannot afford to drift: this is the only time the recovery codes
 * exist anywhere a person can see them.
 *
 * *"i dont like that user have to scroll the page, cant it be a stepper design with
 * next next"* — the three steps were once all on screen at once, which on a laptop
 * meant scrolling past two of them to reach the third. Each step now fits a window on
 * its own, and **Back works**: somebody on the last step has to be able to go and read
 * the recovery codes again. They stop existing the moment the sixth digit lands.
 */
export function MfaEnrolment({
  enrolment,
  enrolStep,
  setEnrolStep,
  typing,
  setTyping,
  code,
  setCode,
  onSubmitCode,
  confirming,
  refused,
  banner,
}: {
  enrolment: Enrolment
  enrolStep: number
  setEnrolStep: Dispatch<SetStateAction<number>>
  typing: boolean
  setTyping: (v: boolean) => void
  code: string
  setCode: (v: string) => void
  onSubmitCode: () => void
  confirming: boolean
  refused: string | null
  /** Said above the first step, when there is something worth saying before it. Omitted
   * shows nothing. */
  banner?: string
}) {
  return (
    <Stack gap={5}>
      {banner && <StatusBanner tone="success">{banner}</StatusBanner>}
      <Stack gap={2}>
        <Row justify="between" className="items-baseline gap-3">
          <Text variant="body-strong" as="h2">
            Set up your authenticator app
          </Text>
          <Text variant="caption" className="text-ink-muted shrink-0 tabular-nums">
            {stepCount(enrolStep)}
          </Text>
        </Row>
        {/* A rule that fills up. `aria-hidden` because the count beside the heading
            already says it in words. */}
        <div aria-hidden className="bg-hairline h-1 w-full overflow-hidden rounded-full">
          <div
            className="bg-coral-fill h-full rounded-full transition-[width] duration-200"
            style={{ width: `${((enrolStep + 1) / ENROL_STEPS.length) * 100}%` }}
          />
        </div>
      </Stack>

      <Text variant="body-strong" as="h3">
        {ENROL_STEPS[enrolStep]!.title}
      </Text>

      {enrolStep === 0 && (
        <Stack gap={3}>
          <Text variant="secondary">{SCAN_IT}</Text>
          {/* `self-start`, or the white plate stretches the column and the code sits in
              a field of white twice its own width. */}
          {/**
           * A full-width plate with the code centred in it.
           *
           * It hugged the code before, which left a square sitting against the left
           * edge with a column of dead space beside it — *"i feel qr code steps looks
           * very asymmetric"*, and it was. A plate that owns the width has nothing
           * beside it to be lopsided against.
           *
           * 176 rather than the component's 168: the plate is the width of the card
           * now, so the code can afford to be the biggest thing in the step, which is
           * what it is.
           */}
          <QRCode
            value={enrolment.uri}
            size={176}
            label="Set-up code for your authenticator app"
            className="w-full justify-center py-4"
          />
          {typing ? (
            /* A QR is nothing to a screen reader and some apps still want it typed, so
               the key is always reachable — one press away rather than absent. */
            <SecretCodes codes={[enrolment.secret]} label="Set-up key" note={TYPE_THE_KEY} />
          ) : (
            /* Centred under the plate it belongs to. It was `self-start` while the
               plate hugged the code — centred under a square pushed to the left, it
               read as a caption for nothing. With the plate owning the width, the two
               are one block and centring is what makes them look like one. */
            <Button variant="tertiary" size="sm" className="self-center" onClick={() => setTyping(true)}>
              {NO_CAMERA}
            </Button>
          )}
        </Stack>
      )}

      {enrolStep === 1 && <SecretCodes codes={enrolment.recovery_codes} label="Recovery codes" />}

      {enrolStep === 2 && (
        <CodeField
          value={code}
          onChange={setCode}
          onSubmit={onSubmitCode}
          refused={refused}
          loading={confirming}
          label="Finish signing in"
        />
      )}

      {/* The last step's own button is "Finish signing in", inside `CodeField` — so the
          strip below it carries only Back. */}
      <Row justify="between" className="gap-3">
        <Button variant="tertiary" disabled={enrolStep === 0} onClick={() => setEnrolStep((n) => n - 1)}>
          Back
        </Button>
        {ENROL_STEPS[enrolStep]!.next && (
          <Button onClick={() => setEnrolStep((n) => n + 1)}>{ENROL_STEPS[enrolStep]!.next}</Button>
        )}
      </Row>
    </Stack>
  )
}
